import { isAxiosError } from "axios";
import { marked } from "marked";
import {
  ExternalValidationFlowError,
  type ExternalValidationAdapter,
} from "@sdt/render-form-engine";
import { HtmlContent } from "@/components/common/HtmlContent";
import { validateFieldFiles } from "@/queries/formality-request/intern-forms.api";

// 502: el flujo falló. 504: no respondió en 60 s. Su `message` se muestra.
const FLOW_ERROR_STATUSES = new Set([502, 504]);

const isAbortError = (err: unknown) =>
  (err instanceof DOMException && err.name === "AbortError") ||
  (isAxiosError(err) && err.code === "ERR_CANCELED");

const getFlowErrorMessage = (err: unknown): string | undefined => {
  if (!isAxiosError(err) || !FLOW_ERROR_STATUSES.has(err.response?.status ?? 0))
    return undefined;
  const message = (err.response?.data as { message?: unknown } | undefined)
    ?.message;
  return typeof message === "string" && message.trim() ? message : undefined;
};

/** El mensaje del flujo es markdown; el HTML pasa por `sanitizeHTML`. */
export const renderValidationMessage = (markdown: string) => (
  <HtmlContent
    html={marked.parse(markdown, { async: false })}
    className="whitespace-normal"
  />
);

/**
 * Adaptador del `ExternalValidationProvider` del motor sobre
 * `POST /admin/v1/internal/forms/assignments/{id}/fields/{fieldId}/validate`.
 * Valida los archivos que el campo ya tiene adjuntos. Un 502/504 rechaza con
 * el mensaje del backend (el motor lo marca como error); cualquier otro
 * fallo, con un error genérico (el motor muestra su mensaje fijo). En ambos
 * casos el avance queda bloqueado.
 */
export const createFormValidationAdapter = (): ExternalValidationAdapter => ({
  validateField: async (assignmentId, fieldId, { signal }) => {
    try {
      return await validateFieldFiles(assignmentId, fieldId, { signal });
    } catch (err) {
      if (isAbortError(err)) throw err;
      const flowMessage = getFlowErrorMessage(err);
      if (flowMessage) {
        throw new ExternalValidationFlowError(flowMessage, { cause: err });
      }
      throw err;
    }
  },
  renderMessage: renderValidationMessage,
});
